import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';

import { GetProfileApiResponse, PatchProfileInfoApiParams } from '../api';

import { clientAxios } from '@/shared/api';
import { ApiResponse } from '@/shared/types';
import {
  GetProfileInfoApiResponse,
  ProfileInfoQueryResponse,
} from '@/shared/types/Profile/info.types';

interface useProfileQueryParams {
  yozmitProfileId?: number;
}

export const useProfileQuery = ({ yozmitProfileId }: useProfileQueryParams) => {
  return useQuery({
    queryKey: ['getProfileApi', yozmitProfileId],
    queryFn: async () => {
      const { data } = await clientAxios.get<
        ApiResponse<GetProfileApiResponse>
      >('/getProfileApi/', { params: { yozmitProfileId } });

      const { id, image, name, role, role_kor } = data.data;

      return {
        id,
        imageUrl: image,
        name,
        role,
        roleKor: role_kor,
      };
    },
    enabled: !!yozmitProfileId,
  });
};

export const useProfileInfoQuery = <useSuspenseType extends boolean>({
  yozmitProfileId,
  useSuspense = false as useSuspenseType,
}: useProfileQueryParams & { useSuspense?: useSuspenseType }) => {
  const options = queryOptions({
    queryKey: ['getProfileInfoApi', yozmitProfileId],
    queryFn: async () => {
      const { data } = await clientAxios.get<
        ApiResponse<GetProfileInfoApiResponse>
      >('/getProfileInfoApi/', {
        params: { yozmitProfileId },
      });

      return {
        role: data.data.role,
        level: data.data.level,
        career: data.data.total_career_year,
        skills: data.data.skills.map((item) => ({
          id: item.id,
          imageUrl: item.image_url,
          name: item.title,
        })),
        activityScorePercentages: data.data.activity_score_percentage,
      };
    },
    enabled: !!yozmitProfileId,
  });

  // TODO: queryKey 리팩터링 시 useSuspense parameter 제거
  const queryFunction = useSuspense ? useSuspenseQuery : useQuery;
  return queryFunction(options) as useSuspenseType extends true
    ? UseSuspenseQueryResult<ProfileInfoQueryResponse>
    : UseQueryResult<ProfileInfoQueryResponse>;
};

export const useProfileInfoMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: PatchProfileInfoApiParams) => {
      const isValidValue = (value: unknown) => {
        if (Array.isArray(value)) return value.length > 0;

        return value != null && value !== '' && value !== 0;
      };

      const cleanedParams = Object.fromEntries(
        Object.entries(params).filter(([_, value]) => isValidValue(value))
      );

      return clientAxios.patch('/patchProfileInfoApi/', cleanedParams);
    },
    onSuccess: async (_, params: PatchProfileInfoApiParams) => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['getProfileInfoApi', params.yozmitProfileId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['getProfileApi', params.yozmitProfileId],
        }),
      ]);
    },
  });
};
